import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { Message } from '../../types';
import { FiPlay, FiPause, FiCopy } from 'react-icons/fi';
import rehypeRaw from 'rehype-raw';

const MESSAGE_MIN_WIDTH = '220px';

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 8px;
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb #f9fafb;
  &::-webkit-scrollbar {
    width: 8px;
    background: #f9fafb;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: #f9fafb;
  }
`;

const MessageItem = styled.div<{ isUser: boolean }>`
  margin-bottom: 8px;
  display: flex;
  flex-direction: ${props => props.isUser ? 'row-reverse' : 'row'};
  align-items: flex-start;
  gap: 8px;
`;

const MessageBubble = styled.div<{ isUser: boolean }>`
  max-width: 80%;
  min-width: ${MESSAGE_MIN_WIDTH};
  display: inline-block;
  padding: 12px 16px;
  border-radius: 18px;
  background: ${props => props.isUser ? '#232B3A' : '#E89B3C'};
  color: ${props => props.isUser ? 'white' : '#232B3A'};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  word-break: break-word;
  white-space: normal;
  line-height: 1.4;
  margin: 0;
  flex-shrink: 0;
  border: ${props => props.isUser ? '2px solid #FFD700' : '2px solid #FFD700'};
  font-size: 14px;
  align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
`;

const MessageText = styled.div`
  margin-bottom: 8px;
  position: relative;
  
  &:hover .copy-button {
    opacity: 1;
    visibility: visible;
  }
`;

const CopyButton = styled.button`
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const MessageTime = styled.div<{ isUser: boolean }>`
  font-size: 11px;
  opacity: 0.8;
  color: rgba(35, 43, 58, 0.8);
  margin-top: 2px;
  margin-bottom: 8px;
  text-align: ${props => props.isUser ? 'right' : 'left'};
  padding: 0 8px;
`;

const VoiceMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const VoiceButton = styled.button`
  background: #FFD700;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #232B3A;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #FF9900;
    color: #fff;
  }
`;

const Transcription = styled.div`
  font-style: italic;
  opacity: 0.8;
  margin-bottom: 8px;
  color: #FF9900;
`;

const ImageContainer = styled.div`
  margin-bottom: 8px;
  border-radius: 8px;
  overflow: hidden;
  max-width: 300px;
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const TypingBubble = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 15px;
  padding: 8px 14px;
  color: #000000ff;
  font-size: 13px;
  font-style: italic;
  max-width: fit-content;
`;



const ImageLabel = styled.div`
  font-size: 12px;
  opacity: 0.85;
  font-weight: 500;
  margin-bottom: 4px;
  color: #FF9900;
`;

const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #FFD700;
  border-radius: 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  color: #232B3A;
  font-style: italic;
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 4px;
`;

const Dot = styled.div<{ delay: number }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #6b7280;
  animation: pulse 1.4s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;

  @keyframes pulse {
    0%, 80%, 100% {
      opacity: 0.3;
      transform: scale(0.8);
    }
    40% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6b7280;
  text-align: center;
  padding: 32px;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyText = styled.div`
  font-size: 14px;
  line-height: 1.5;
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  margin: 12px 0 8px 8px;
`;

const TypingDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  background: #A0AEC0;
  border-radius: 50%;
  opacity: 0.8;
  animation: typing-bounce 1.2s infinite;

  &:nth-child(2) {
    animation-delay: 0.2s;
  }
  &:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes typing-bounce {
    0%, 80%, 100% {
      transform: scale(0.8);
      opacity: 0.5;
    }
    40% {
      transform: scale(1.2);
      opacity: 1;
    }
  }
`;

interface ChatWindowProps {
  messages: Message[];
  loading: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  onRecord?: () => void;
  t: (key: string) => string;   // ✅ add translation function
}



const ChatWindow: React.FC<ChatWindowProps> = ({ messages, loading, messagesEndRef, onRecord,t }) => {
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement }>({});
const [typingText, setTypingText] = useState("K9 Coach is thinking");

  console.log("Sada",messages);

  useEffect(() => {
  if (!loading) {
    setTypingText("K9 Coach is thinking");
    return;
  }

  setTypingText("K9 Coach is thinking");

  const timer1 = setTimeout(() => {
    setTypingText("K9 Coach is fetching response");
  }, 5000);

  const timer2 = setTimeout(() => {
    setTypingText("K9 Coach is responding");
  }, 10000);

  return () => {
    clearTimeout(timer1);
    clearTimeout(timer2);
  };
}, [loading]);

  
  // Helper function to check if audio URL is valid and safe to create
  const isAudioUrlSafe = (url: string): boolean => {
    if (!url) return false;
    // Allow all audio URLs - we'll handle errors gracefully instead of blocking
    return true;
  };
  
  // Track if we're transitioning from guest to user to prevent audio errors
  const [isUserTransition, setIsUserTransition] = useState(false);
  const prevUserRef = useRef<any>(null);
  
  // Detect user transition (guest to authenticated user)
  useEffect(() => {
    const hasUserChanged = prevUserRef.current !== null && 
                          prevUserRef.current !== messages[0]?.userMessage && 
                          messages.length > 0;
    
    if (hasUserChanged) {
      setIsUserTransition(true);
      // Clear audio refs to prevent playing invalid guest audio
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
      audioRefs.current = {};
      setPlayingAudio(null);
      
      // Reset transition flag after a short delay
      setTimeout(() => setIsUserTransition(false), 1000);
    }
    
    prevUserRef.current = messages[0]?.userMessage;
  }, [messages]);

  const testAudioUrl = async (audioUrl: string): Promise<boolean> => {
    try {
      console.log('Testing audio URL accessibility:', audioUrl);
      
      const response = await fetch(audioUrl, {
        method: 'HEAD',
        mode: 'cors',
        cache: 'no-cache'
      });
      
      console.log('Audio URL test response:', {
        url: audioUrl,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      if (response.status === 405) {
        // Some servers do not support HEAD, treat as accessible
        return true;
      }
      
      return response.ok;
    } catch (error) {
      let errorMessage = '';
      let errorStack = '';
      if (typeof error === 'object' && error !== null) {
        errorMessage = (error as any).message || String(error);
        errorStack = (error as any).stack || '';
      } else {
        errorMessage = String(error);
      }
      console.error('Audio URL test failed:', {
        url: audioUrl,
        error: errorMessage,
        stack: errorStack
      });
      return false;
    }
  };

  // Helper to rewrite audio URLs to use the HTTPS proxy if needed
  const getPlayableAudioUrl = (audioUrl: string) => {
    // If it's a base64 data URL (Vercel), use it directly
    if (audioUrl.startsWith('data:audio/')) {
      return audioUrl;
    }
    
    // If it's an S3 URL, use it directly (no proxy needed)
    if (audioUrl.includes('s3.amazonaws.com') || audioUrl.includes('s3.')) {
      return audioUrl;
    }
    
    // If it's a local URL (starts with /voice/), use it directly
    if (audioUrl.startsWith('/voice/')) {
      const base = window.location.origin;
      return `${base}${audioUrl}`;
    }

    if (audioUrl.startsWith("/")) {
    return `${process.env.NEXT_PUBLIC_API_BASE_URL || "http://89.117.60.217:6471"}${audioUrl}`;
  }
    
    // Always proxy if the URL is from your audio server to avoid CORS issues
    if (audioUrl.includes('89.117.60.217:6471')) {
      const base = window.location.origin;
      // Always use http in the proxy URL
      const httpUrl = audioUrl.replace(/^https:/, 'http:');
      return `${base}/api/proxy-audio?url=${encodeURIComponent(httpUrl)}`;
    }
    
    return audioUrl;
  };

  const handlePlayAudio = async (messageId: number, audioUrl: string, isUserMessage?: boolean) => {
    // Prevent audio playback during user transition to avoid errors
    if (isUserTransition) {
      console.log('Skipping audio playback during user transition');
      return;
    }
    
    const audioId = `audio-${messageId}-${isUserMessage ? 'user' : 'bot'}`;
    
    console.log('Attempting to play audio:', {
      messageId,
      audioId,
      isUserMessage,
      currentPlaying: playingAudio
    });
    
    // For blob URLs (user voice messages), use directly
    // For external URLs (bot voice messages), use proxy
    const finalUrl = audioUrl.startsWith('blob:') ? audioUrl : getPlayableAudioUrl(audioUrl);
    
    console.log('Audio URL processing:', {
      originalUrl: audioUrl,
      finalUrl: finalUrl,
      isBlob: audioUrl.startsWith('blob:'),
      isUserMessage: isUserMessage
    });
    
    // Test if the audio URL is accessible (skip for blob URLs, base64 URLs, and S3 URLs)
    if (!audioUrl.startsWith('blob:') && !audioUrl.startsWith('data:audio/') && !audioUrl.includes('s3.amazonaws.com') && !audioUrl.includes('s3.')) {
      const isAccessible = await testAudioUrl(finalUrl);
      console.log('Audio accessibility test:', {
        url: finalUrl,
        isAccessible: isAccessible
      });
      
             if (!isAccessible) {
         console.log('Audio file is not accessible, but continuing anyway');
         // Don't return - let it try to play anyway
       }
    }
    
    if (playingAudio === audioId) {
      // Stop current audio
      if (audioRefs.current[audioId]) {
        audioRefs.current[audioId].pause();
        audioRefs.current[audioId].currentTime = 0;
      }
      setPlayingAudio(null);
    } else {
      // Stop any other playing audio
      if (playingAudio && audioRefs.current[playingAudio]) {
        audioRefs.current[playingAudio].pause();
        audioRefs.current[playingAudio].currentTime = 0;
      }
      
      // Play new audio
      if (audioRefs.current[audioId]) {
        const audioElement = audioRefs.current[audioId];
        
        console.log('Audio element found:', {
          audioId: audioId,
          currentSrc: audioElement.src,
          newSrc: finalUrl,
          readyState: audioElement.readyState,
          networkState: audioElement.networkState
        });
        
        // Only update src if it's different to avoid unnecessary reloads
        if (audioElement.src !== finalUrl) {
          audioElement.src = finalUrl;
          console.log('Audio src updated to:', finalUrl);
        }
        
                 // Add error handling
         audioElement.onerror = (e) => {
           console.error('Audio playback error:', e);
           console.error('Audio URL:', finalUrl);
           console.error('Audio element:', audioElement);
           console.error('Audio error details:', {
             error: audioElement.error,
             errorCode: audioElement.error?.code,
             errorMessage: audioElement.error?.message
           });
           
                       // Don't show alert for blob URL errors or during user transitions
            if (!finalUrl.startsWith('blob:') && !isUserTransition) {
              console.log('Audio playback failed, but suppressing alert during user transition');
            }
           setPlayingAudio(null);
         };
        
        // Add load event
        audioElement.onloadeddata = () => {
          console.log('Audio loaded successfully:', {
            duration: audioElement.duration,
            readyState: audioElement.readyState,
            networkState: audioElement.networkState
          });
        };
        
        // Add minimal event listeners for debugging
        audioElement.onloadstart = () => console.log('Audio load started');
        audioElement.oncanplay = () => console.log('Audio can play');
        audioElement.onload = () => console.log('Audio load complete');
        
        // Try to play
        console.log('Attempting to play audio element...');
        
        // Ensure audio is loaded before playing
        if (audioElement.readyState >= 2) { // HAVE_CURRENT_DATA
          audioElement.play().then(() => {
            console.log('Audio started playing successfully');
            setPlayingAudio(audioId);
                     }).catch((error) => {
             console.error('Failed to play audio:', error);
             console.error('Error details:', {
               name: error.name,
               message: error.message,
               stack: error.stack
             });
             // Only show alert if not during user transition
             if (!isUserTransition) {
               alert('Failed to play audio. Please try again.');
             }
             setPlayingAudio(null);
           });
        } else {
          // Wait for audio to load
          audioElement.oncanplay = () => {
            console.log('Audio can now play, attempting playback...');
            audioElement.play().then(() => {
              console.log('Audio started playing successfully');
              setPlayingAudio(audioId);
                         }).catch((error) => {
               console.error('Failed to play audio after loading:', error);
               // Only show alert if not during user transition
               if (!isUserTransition) {
                 alert('Failed to play audio. Please try again.');
               }
               setPlayingAudio(null);
             });
          };
          
          // Load the audio
          audioElement.load();
        }
      } else {
        console.error('Audio element not found for ID:', audioId);
        console.log('Available audio refs:', Object.keys(audioRefs.current));
        console.log('Current audio refs state:', audioRefs.current);
      }
    }
  };

  const handleAudioEnded = () => {
    setPlayingAudio(null);
  };

  // Cleanup audio refs only when component unmounts
  useEffect(() => {
    return () => {
      // Clean up audio elements to prevent memory leaks
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
      audioRefs.current = {};
    };
  }, []); // Only run on unmount

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here if you want
      console.log('Text copied to clipboard');
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  if (messages.length === 0 && !loading) {
    return (
<MessagesContainer>
      <EmptyState>
        <EmptyIcon>🐕</EmptyIcon>
        <EmptyText>
          <div>{t("welcome")}</div>
          <div style={{ fontSize: '12px', marginTop: '8px' }}>
            {t("askAnything")}
          </div>
        </EmptyText>
      </EmptyState>
    </MessagesContainer>
    );
  }

  console.log('ChatWindow rendering with messages:', messages.length, 'audio refs:', Object.keys(audioRefs.current).length);
  
  return (
    <MessagesContainer>
      {messages.map((message) => {
        // Log for voice and image messages to debug audio issues
        if (message.type === 'voice' || message.type === 'image') {
          console.log(`Rendering ${message.type} message:`, {
            id: message.id,
            hasUserMessage: !!message.userMessage,
            hasBotResponse: !!message.botResponse,
            hasAudioUrl: !!message.audioUrl,
            audioUrl: message.audioUrl,
            hasTranscription: !!message.transcription,
            userAudioUrl: !!message.userAudioUrl,
            hasImageUrl: !!message.imageUrl
          });
        }
        
        return (
          <React.Fragment key={message.id}>
            {/* User Message */}
            {message.userMessage && (
              <MessageItem isUser={true}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <MessageBubble isUser={true}>
                    {message.type === 'voice' && message.transcription && (
                      <Transcription>
                        "{message.transcription}"
                      </Transcription>
                    )}
                    {message.type === 'image' && message.imageUrl && (
                      <>
                        <ImageLabel>Upload Image</ImageLabel>
                        <ImageContainer>
                          <img src={message.imageUrl} alt="User uploaded" />
                        </ImageContainer>
                      </>
                    )}
                                         <MessageText>
                       {message.userMessage}
                       <CopyButton 
                         className="copy-button"
                         onClick={() => copyToClipboard(message.userMessage || '')}
                         title="Copy message"
                       >
                         <FiCopy />
                       </CopyButton>
                     </MessageText>
                    {/* User voice message with play button and label, styled exactly like bot's */}
                    {message.type === 'voice' && (message.userAudioUrl || message.audioUrl) && (
                      <VoiceMessage>
                        <VoiceButton
                          onClick={() => handlePlayAudio(message.id, message.userAudioUrl || message.audioUrl!, true)}
                        >
                          {playingAudio === `audio-${message.id}-user` ? <FiPause /> : <FiPlay />}
                        </VoiceButton>
                        <div style={{ fontSize: '12px', opacity: 0.85, fontWeight: 500, marginRight: 8 }}>
                          Voice message
                        </div>
                        <div style={{ fontSize: '11px', color: '#b0b8c1', minWidth: 32 }}>
                          {audioRefs.current[`audio-${message.id}-user`]?.duration
                            ? `${Math.floor(audioRefs.current[`audio-${message.id}-user`].duration / 60)}:${String(Math.floor(audioRefs.current[`audio-${message.id}-user`].duration % 60)).padStart(2, '0')}`
                            : '--:--'}
                        </div>
                      </VoiceMessage>
                    )}
                  </MessageBubble>
                  <MessageTime isUser={true}>
                    {formatTime(message.timestamp)}
                  </MessageTime>
                                                           {/* Hidden audio element for user voice messages */}
                      {message.type === 'voice' && (message.userAudioUrl || message.audioUrl) && (
                     <audio
                       ref={(el) => {
                         if (el) {
                           audioRefs.current[`audio-${message.id}-user`] = el;
                           console.log('Audio ref set for user message:', {
                             messageId: message.id,
                             audioId: `audio-${message.id}-user`,
                             audioUrl: message.userAudioUrl || message.audioUrl,
                             totalRefs: Object.keys(audioRefs.current).length
                           });
                         } else {
                           console.log('Audio ref cleared for user message:', {
                             messageId: message.id,
                             audioId: `audio-${message.id}-user`
                           });
                         }
                       }}
                       src={message.userAudioUrl || message.audioUrl}
                       onEnded={handleAudioEnded}
                       onError={(e) => {
                         console.error('Audio element error:', {
                           messageId: message.id,
                           audioUrl: message.userAudioUrl || message.audioUrl,
                           error: e
                         });
                       }}
                       style={{ display: 'none' }}
                     />
                   )}
                </div>
              </MessageItem>
            )}
            {/* Bot Response */}
            {message.botResponse && (
              <MessageItem isUser={false}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <MessageBubble isUser={false}>
                                         <MessageText>
                       <ReactMarkdown
                         rehypePlugins={[rehypeRaw]}
                         components={{
                           a: ({ node, ...props }) => (
                             <a
                               {...props}
                               target="_blank"
                               rel="noopener noreferrer"
                               style={{
                                 color: '#0066cc'
                               }}
                             />
                           )
                         }}
                       >
                         {message.botResponse}
                       </ReactMarkdown>
                       <CopyButton 
                         className="copy-button"
                         onClick={() => copyToClipboard(message.botResponse || '')}
                         title="Copy message"
                       >
                         <FiCopy />
                       </CopyButton>
                     </MessageText>
                    {message.type === 'image' && message.audioUrl && (
                      <VoiceMessage>
                        <VoiceButton
                          onClick={() => handlePlayAudio(message.id, getPlayableAudioUrl(message.audioUrl!), false)}
                        >
                          {playingAudio === `audio-${message.id}-bot` ? <FiPause /> : <FiPlay />}
                        </VoiceButton>
                        <div style={{ fontSize: '12px', opacity: 0.85, fontWeight: 500, marginRight: 8 }}>
                          Voice response
                        </div>
                        <div style={{ fontSize: '11px', color: '#232B3A', minWidth: 32 }}>
                          {audioRefs.current[`audio-${message.id}-bot`]?.duration
                            ? `${Math.floor(audioRefs.current[`audio-${message.id}-bot`].duration / 60)}:${String(Math.floor(audioRefs.current[`audio-${message.id}-bot`].duration % 60)).padStart(2, '0')}`
                            : '--:--'}
                        </div>
                      </VoiceMessage>
                    )}
                    {message.type === 'voice' && message.audioUrl && (
                      <VoiceMessage>
                        <VoiceButton
                          onClick={() => handlePlayAudio(message.id, getPlayableAudioUrl(message.audioUrl!), false)}
                        >
                          {playingAudio === `audio-${message.id}-bot` ? <FiPause /> : <FiPlay />}
                        </VoiceButton>
                        <div style={{ fontSize: '12px', opacity: 0.85, fontWeight: 500, marginRight: 8 }}>
                          Voice message
                        </div>
                        <div style={{ fontSize: '11px', color: '#232B3A', minWidth: 32 }}>
                          {audioRefs.current[`audio-${message.id}-bot`]?.duration
                            ? `${Math.floor(audioRefs.current[`audio-${message.id}-bot`].duration / 60)}:${String(Math.floor(audioRefs.current[`audio-${message.id}-bot`].duration % 60)).padStart(2, '0')}`
                            : '--:--'}
                        </div>
                      </VoiceMessage>
                    )}
                     {(() => {
                      const videos = message.recommendedVideos || message.video_recommendations;
                      if (videos && Array.isArray(videos) && videos.length > 0) {
                        const video = videos[0];
                        let thumbnailUrl = '';
                        if (video.video_url.includes('youtube.com') || video.video_url.includes('youtu.be')) {
                          const match = video.video_url.match(/(?:v=|\/)([\w-]{11})(?:[&?]|$)/);
                          const id = match ? match[1] : '';
                          if (id) thumbnailUrl = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
                        } else if (video.video_url.includes('dailymotion.com')) {
                          const match = video.video_url.match(/video\/([\w]+)/);
                          const id = match ? match[1] : '';
                          if (id) thumbnailUrl = `https://www.dailymotion.com/thumbnail/video/${id}`;
                        }
                        // fallback: no thumbnail for other sources
                        return (
                          <div style={{ width: '100%', marginTop: 12 }}>
                            <a href={video.video_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', width: '100%' }}>
                              {thumbnailUrl ? (
                                <img src={thumbnailUrl} alt={video.title} style={{ width: '100%', borderRadius: 8, objectFit: 'cover', maxHeight: 180 }} />
                              ) : (
                                <div style={{ width: '100%', height: 100, background: '#eee', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 14 }}>
                                  Video
                                </div>
                              )}
                            </a>
                            {video.title && (
                              <div style={{ fontWeight: 'bold', color: '#e89b3c', marginTop: 6 }}>{video.title}</div>
                            )}
                            {video.summary && (
                              <div style={{ fontSize: 13, color: '#444', marginTop: 2 }}>{video.summary}</div>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </MessageBubble>

                  <MessageTime isUser={false}>
                    {formatTime(message.timestamp)}
                  </MessageTime>
                                                           {/* Hidden audio element for voice messages */}
                      {message.type === 'voice' && message.audioUrl && (
                     <audio
                       ref={(el) => {
                         if (el) {
                           audioRefs.current[`audio-${message.id}-bot`] = el;
                           console.log('Audio ref set for bot message:', {
                             messageId: message.id,
                             audioId: `audio-${message.id}-bot`,
                             audioUrl: message.audioUrl,
                             processedUrl: message.audioUrl ? getPlayableAudioUrl(message.audioUrl) : 'undefined',
                             totalRefs: Object.keys(audioRefs.current).length
                           });
                         } else {
                           console.log('Audio ref cleared for bot message:', {
                             messageId: message.id,
                             audioId: `audio-${message.id}-bot`
                           });
                         }
                       }}
                       src={getPlayableAudioUrl(message.audioUrl)}
                       onEnded={handleAudioEnded}
                       onError={(e) => {
                         console.error('Audio element error:', {
                           messageId: message.id,
                           audioUrl: message.audioUrl,
                           error: e
                         });
                       }}
                       style={{ display: 'none' }}
                     />
                   )}
                                                           {/* Hidden audio element for image messages */}
                      {message.type === 'image' && message.audioUrl && (
                     <audio
                       ref={(el) => {
                         if (el) {
                           audioRefs.current[`audio-${message.id}-bot`] = el;
                           console.log('Audio ref set for image bot message:', {
                             messageId: message.id,
                             audioId: `audio-${message.id}-bot`,
                             audioUrl: message.audioUrl,
                             processedUrl: message.audioUrl ? getPlayableAudioUrl(message.audioUrl) : 'undefined',
                             totalRefs: Object.keys(audioRefs.current).length
                           });
                         } else {
                           console.log('Audio ref cleared for image bot message:', {
                             messageId: message.id,
                             audioId: `audio-${message.id}-bot`
                           });
                         }
                       }}
                       src={getPlayableAudioUrl(message.audioUrl)}
                       onEnded={handleAudioEnded}
                       onError={(e) => {
                         console.error('Audio element error for image message:', {
                           messageId: message.id,
                           audioUrl: message.audioUrl,
                           error: e
                         });
                       }}
                       style={{ display: 'none' }}
                     />
                   )}
                </div>
              </MessageItem>
            )}
          </React.Fragment>
        );
      })}
      <div ref={messagesEndRef} />
{loading && (
  <TypingIndicator>
    <TypingBubble>
      <div style={{ display: 'flex', gap: 4 }}>
        <TypingDot />
        <TypingDot />
        <TypingDot />
      </div>
      <span>{typingText}</span>
    </TypingBubble>
  </TypingIndicator>
)}


    </MessagesContainer>
  );
};

export default ChatWindow; 
